import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { Brand } from '../../components/Brand';
import { SUGGESTED_PROMPTS } from '../data';
import type { ChatMessage, Conversation } from '../types';
import {
  blockToText,
  getConversation,
  listConversations,
  mapConversation,
  mapConversationSummary,
  sendMessage,
} from '../../lib/entities/conversations';
import { useActiveCompany } from '../state';
import { ArrowRight, Padlock, Person, Plus, Send, Shield, Sparkle, Tick } from '../components/AppIcons';
import '../../styles/app-assistant.css';

const WELCOME_TEXT =
  'Ask me anything, like contracts, tricky conversations or what the law expects. I’ll answer from your documents and current UK law.';

/** Topics heavy enough to suggest handing over to a person. */
const ESCALATION = /grievance|disciplinar|redundan/i;

/** Drafts live client-side until the server mints a real conversation id. */
function isDraftId(id: string): boolean {
  return id.startsWith('local-');
}

/** Expiry line for a conversation, honouring a locally flipped Keep toggle. */
export function expiryLabel(conv: Conversation): string {
  if (conv.saved) return 'Saved · won’t expire';
  return conv.expiresIn;
}

function trimTitle(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > 44 ? `${clean.slice(0, 42).trimEnd()}…` : clean;
}

function freshConversation(id: string): Conversation {
  return {
    id,
    title: 'New conversation',
    startedAt: 'Just now',
    expiresIn: 'Expires in 30 days',
    messages: [],
  };
}

/** Wrap a backend/stream error as an assistant-side notice, never a crash. */
function friendlyError(error: string): string {
  return `Sorry, I can’t answer right now. ${error}`;
}

function EscalationHint() {
  return (
    <Link to="/app/cases" className="ast-escalate">
      <span className="ast-escalate__icon">
        <Person size={16} />
      </span>
      Serious situation? Hand it to a CIPD-qualified expert.
      <ArrowRight size={14} className="ast-escalate__arrow" />
    </Link>
  );
}

export default function AssistantPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useActiveCompany();

  /** Server summaries (agent "chat") plus any local drafts, newest first. */
  const [conversations, setConversations] = useState<Conversation[]>([]);
  /** Messages per conversation id; presence doubles as the "loaded" flag. */
  const [messagesByConv, setMessagesByConv] = useState<Record<string, ChatMessage[]>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  /** The reply stream in flight, with the latest status line for the indicator. */
  const [streaming, setStreaming] = useState<{ convId: string; status: string | null } | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [loadingConvId, setLoadingConvId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const nextConvId = useRef(1);
  const nextMsgId = useRef(1);
  const bootedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const lastScrolledConv = useRef('');

  // sendTo runs from closures created at boot (the ?q= flow), so read the
  // active company through a ref to avoid anchoring to a stale value.
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  const active = conversations.find((c) => c.id === activeId) ?? null;
  const activeMessages = (activeId ? messagesByConv[activeId] : undefined) ?? [];
  const typing = streaming?.convId === activeId;
  const convLoading = loadingConvId !== null && loadingConvId === activeId;
  const showPrompts =
    !!active && !typing && !convLoading && !activeMessages.some((m) => m.role === 'user');

  const upsertMessage = useCallback((convId: string, msg: ChatMessage) => {
    setMessagesByConv((prev) => {
      const list = prev[convId] ?? [];
      const idx = list.findIndex((m) => m.id === msg.id);
      const next = idx === -1 ? [...list, msg] : list.map((m, i) => (i === idx ? msg : m));
      return { ...prev, [convId]: next };
    });
  }, []);

  /** Refresh the sidebar from the server, keeping drafts and Keep flips. */
  const refreshList = useCallback(async () => {
    const dtos = await listConversations();
    const mapped = dtos
      .filter((d) => d.agentId === 'chat')
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
      .map(mapConversationSummary);
    setConversations((prev) => {
      const savedById = new Map(prev.map((c) => [c.id, c.saved]));
      const drafts = prev.filter((c) => isDraftId(c.id));
      return [...drafts, ...mapped.map((c) => ({ ...c, saved: savedById.get(c.id) }))];
    });
  }, []);

  const createDraft = useCallback((): Conversation => {
    const conv = freshConversation(`local-${nextConvId.current++}`);
    setConversations((prev) => [conv, ...prev]);
    setMessagesByConv((prev) => ({
      ...prev,
      [conv.id]: [{ id: `${conv.id}-welcome`, role: 'assistant', text: WELCOME_TEXT }],
    }));
    setActiveId(conv.id);
    return conv;
  }, []);

  /** A completed draft becomes the server conversation: move its state over. */
  const promoteDraft = useCallback((localId: string, serverId: string) => {
    setMessagesByConv((prev) => {
      const { [localId]: msgs, ...rest } = prev;
      return { ...rest, [serverId]: msgs ?? [] };
    });
    setConversations((prev) => prev.map((c) => (c.id === localId ? { ...c, id: serverId } : c)));
    setActiveId((prev) => (prev === localId ? serverId : prev));
  }, []);

  const sendTo = useCallback(
    (convId: string, raw: string) => {
      const text = raw.trim();
      if (!text) return;

      const userMsg: ChatMessage = { id: `local-m${nextMsgId.current++}`, role: 'user', text };
      upsertMessage(convId, userMsg);
      // Optimistic title for a fresh draft; the server's title wins on refresh.
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId && c.title === 'New conversation' ? { ...c, title: trimTitle(text) } : c,
        ),
      );

      const isDraft = isDraftId(convId);
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming({ convId, status: null });

      const clearStreaming = () =>
        setStreaming((s) => (s && s.convId === convId ? null : s));

      void sendMessage(
        {
          agentId: 'chat',
          message: text,
          conversationId: isDraft ? undefined : convId,
          // Anchor NEW conversations to the active company (null = general).
          companyId: isDraft ? companyIdRef.current : undefined,
        },
        {
          onStatus: (message) =>
            setStreaming((s) => (s && s.convId === convId ? { ...s, status: message } : s)),
          onBlock: (block) => {
            const blockText = blockToText(block);
            if (!blockText) return;
            upsertMessage(convId, {
              id: `${convId}-blk-${block.id ?? 'only'}`,
              role: 'assistant',
              text: blockText,
            });
          },
          onComplete: (serverId) => {
            clearStreaming();
            if (serverId && isDraft) promoteDraft(convId, serverId);
            if (serverId) void refreshList().catch(() => undefined);
          },
          onError: (error) => {
            clearStreaming();
            upsertMessage(convId, {
              id: `${convId}-err-${nextMsgId.current++}`,
              role: 'assistant',
              text: friendlyError(error),
            });
          },
        },
        controller.signal,
      ).finally(clearStreaming);
    },
    [upsertMessage, promoteDraft, refreshList],
  );

  // Boot: load the conversation list; /app/assistant?q=… also starts a new
  // conversation and sends the question once. The ref guards against
  // StrictMode's double-run of this effect.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const q = searchParams.get('q');
    if (q && q.trim()) {
      const conv = createDraft();
      sendTo(conv.id, q);
      setSearchParams({}, { replace: true });
    }

    void (async () => {
      try {
        await refreshList();
      } catch {
        // List unavailable — the page still works as a fresh conversation.
      } finally {
        setListLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any in-flight stream when leaving the page.
  useEffect(() => () => abortRef.current?.abort(), []);

  // After the list settles: make sure something is active (first conversation,
  // or a fresh draft when the account has none).
  useEffect(() => {
    if (listLoading) return;
    if (conversations.length === 0) {
      createDraft();
      return;
    }
    if (!activeId || !conversations.some((c) => c.id === activeId)) {
      setActiveId(conversations[0].id);
    }
  }, [listLoading, conversations, activeId, createDraft]);

  // Opening a server conversation loads its messages once.
  useEffect(() => {
    if (!activeId || isDraftId(activeId) || messagesByConv[activeId]) return;
    const id = activeId;
    setLoadingConvId(id);
    getConversation(id)
      .then((dto) => {
        const mapped = mapConversation(dto);
        setMessagesByConv((prev) => ({ ...prev, [id]: mapped.messages }));
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Could not load this conversation.';
        setMessagesByConv((prev) => ({
          ...prev,
          [id]: [{ id: `${id}-loaderr`, role: 'assistant', text: friendlyError(message) }],
        }));
      })
      .finally(() => setLoadingConvId((prev) => (prev === id ? null : prev)));
  }, [activeId, messagesByConv]);

  // Keep the newest message in view; jump (not glide) when switching threads.
  useEffect(() => {
    if (!activeId) return;
    const behavior: ScrollBehavior = lastScrolledConv.current === activeId ? 'smooth' : 'auto';
    lastScrolledConv.current = activeId;
    endRef.current?.scrollIntoView({ behavior, block: 'nearest' });
  }, [activeId, activeMessages.length, typing, streaming?.status]);

  const startNewConversation = () => {
    const empty = conversations.find(
      (c) => isDraftId(c.id) && !(messagesByConv[c.id] ?? []).some((m) => m.role === 'user'),
    );
    if (empty) {
      setActiveId(empty.id);
      return;
    }
    createDraft();
  };

  const toggleSaved = (convId: string) => {
    setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, saved: !c.saved } : c)));
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text || streaming || !active) return;
    sendTo(active.id, text);
    setDraft('');
    if (inputRef.current) inputRef.current.style.height = '';
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const autoGrow = (e: FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  };

  return (
    <div className="apage apage--wide">
      <PageHeader
        eyebrow="Assistant"
        title={
          <>
            Ask <Brand /> anything
          </>
        }
        lead="What’s going on?"
      />

      <div className="ast-layout">
        {/* Conversation list */}
        <aside className="ast-side">
          <button type="button" className="btn btn-green-soft btn-xs ast-side__new" onClick={startNewConversation}>
            <Plus size={15} />
            New conversation
          </button>

          <div className="acard ast-convs">
            <div className="ast-convs__head">Conversations</div>
            <div className="ast-convs__list">
              {listLoading && conversations.length === 0 && (
                <div className="ast-conv">
                  <span className="ast-conv__meta">Loading conversations…</span>
                </div>
              )}
              {conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`ast-conv${c.id === activeId ? ' is-active' : ''}`}
                  onClick={() => setActiveId(c.id)}
                >
                  <span className="ast-conv__title">{c.title}</span>
                  <span className="ast-conv__meta">{c.startedAt}</span>
                  {expiryLabel(c) && (
                    <span className={`ast-conv__expiry${c.saved ? ' ast-conv__expiry--saved' : ''}`}>
                      <Padlock size={11} />
                      {expiryLabel(c)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* The chat */}
        <section className="acard ast-chat">
          <header className="ast-chat__head">
            <div className="ast-chat__id">
              <span className="ast-chat__name">
                Ask <Brand />
              </span>
              <span className="ast-online">
                <span className="ast-online__dot" />
                Online
              </span>
            </div>
            {active && (
              <div className="ast-chat__keep">
                {expiryLabel(active) && (
                  <span className={`ast-expiry${active.saved ? ' ast-expiry--saved' : ''}`}>
                    <Padlock size={13} />
                    {expiryLabel(active)}
                  </span>
                )}
                <button
                  type="button"
                  className={`ast-keep${active.saved ? ' is-on' : ''}`}
                  role="switch"
                  aria-checked={!!active.saved}
                  onClick={() => toggleSaved(active.id)}
                >
                  Keep
                  <span className="ast-keep__track">
                    <span className="ast-keep__knob" />
                  </span>
                </button>
              </div>
            )}
          </header>

          <div className="ast-chat__scroll">
            {convLoading && (
              <div className="ast-ground">
                <span className="ast-ground__label">Loading conversation…</span>
              </div>
            )}

            {!convLoading &&
              activeMessages.map((m) => (
                <Fragment key={m.id}>
                  <div className={`ast-msg ast-msg--${m.role === 'user' ? 'user' : 'bot'}`}>{m.text}</div>
                  {m.role === 'assistant' && m.grounding && m.grounding.length > 0 && (
                    <div className="ast-ground">
                      <span className="ast-ground__label">Grounded in:</span>
                      {m.grounding.map((g) => (
                        <span key={g} className="ast-ground__chip">
                          <span className="ast-ground__tick">
                            <Tick size={9} color="#fff" strokeWidth={3.4} />
                          </span>
                          {g}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.role === 'assistant' && ESCALATION.test(m.text) && <EscalationHint />}
                </Fragment>
              ))}

            {typing && (
              <>
                <div className="ast-msg ast-msg--bot ast-typing" aria-label="reeve is typing">
                  <span />
                  <span />
                  <span />
                </div>
                {streaming?.status && (
                  <div className="ast-ground">
                    <span className="ast-ground__label">{streaming.status}</span>
                  </div>
                )}
              </>
            )}

            {showPrompts && (
              <div className="ast-prompts">
                <span className="ast-prompts__label">
                  <Sparkle size={14} />
                  Try asking
                </span>
                <div className="ast-prompts__chips">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="ast-prompts__chip"
                      onClick={() => active && sendTo(active.id, p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          <form className="ast-composer" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              className="ast-composer__input"
              placeholder="Ask about anything…"
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onInput={autoGrow}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              aria-label="Ask reeve"
            />
            <button
              type="submit"
              className="ast-composer__send"
              disabled={!!streaming || !draft.trim() || !active}
              aria-label="Send message"
            >
              <Send size={17} />
            </button>
          </form>
        </section>

        {/* Trust note */}
        <div className="ast-trust">
          <Shield size={16} className="ast-trust__icon" />
          <p className="ast-trust__text">
            Conversations are encrypted and expire after 30 days unless you keep them. They’re never
            used to train AI models.
          </p>
        </div>
      </div>
    </div>
  );
}
